import React from "react";

import type { DateRange } from "../../lib/types";
import { getHashParam, setHashParam } from "../../lib/hashState";
import { Select } from "../../design/Field";
import { Tooltip } from "../../design/Tooltip";

export const RANGE_PRESETS = [
  { value: "1", label: "Today" },
  { value: "7", label: "7d" },
  { value: "30", label: "30d" },
  { value: "mtd", label: "This month" },
  { value: "lm", label: "Last month" },
  { value: "90", label: "90d" },
  { value: "366", label: "12m" },
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number]["value"];

const isoDay = (t: number) => new Date(t).toISOString().slice(0, 10);

/** The last ``days`` calendar days ending today, the month so far, or the
 * whole previous month, in UTC days like the API's buckets. */
export function presetRange(days: RangePreset, today = new Date()): DateRange {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const d = today.getUTCDate();
  if (days === "lm")
    return {
      start: isoDay(Date.UTC(y, m - 1, 1)),
      end: isoDay(Date.UTC(y, m, 0)),
    };
  const start = days === "mtd" ? 1 : d - (Number(days) - 1);
  return { start: isoDay(Date.UTC(y, m, start)), end: isoDay(today.getTime()) };
}

type Shared = [RangePreset, (p: RangePreset) => void];
const SharedRange = React.createContext<Shared | null>(null);

const isPreset = (v: string | null): v is RangePreset =>
  RANGE_PRESETS.some((p) => p.value === v);

/** The ``r`` parameter after the route in the hash: #organization/home?r=7. */
export function hashRange(hash = window.location.hash): RangePreset | null {
  const r = getHashParam("r", hash);
  // Saved links from when the longest preset asked for 400 days.
  if (r === "400") return "366";
  return isPreset(r) ? r : null;
}

/** One time range for every page under it, kept in the URL so a reload or a
 * shared link opens on the same range. */
export function RangeProvider({ children }: { children: React.ReactNode }) {
  const [preset, setPresetState] = React.useState<RangePreset>(
    () => hashRange() ?? "30",
  );
  React.useEffect(() => {
    const sync = () => {
      const r = hashRange();
      if (r) setPresetState(r);
    };
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);
  const setPreset = React.useCallback((p: RangePreset) => {
    setPresetState(p);
    setHashParam("r", p === "30" ? null : p);
  }, []);
  const value = React.useMemo<Shared>(
    () => [preset, setPreset],
    [preset, setPreset],
  );
  return <SharedRange.Provider value={value}>{children}</SharedRange.Provider>;
}

/** The shared range when a RangeProvider is above, else a local one. */
export function usePreset(): [
  RangePreset,
  (p: RangePreset) => void,
  DateRange,
] {
  const shared = React.useContext(SharedRange);
  const local = React.useState<RangePreset>("30");
  const [preset, setPreset] = shared ?? local;
  const range = React.useMemo(() => presetRange(preset), [preset]);
  return [preset, setPreset, range];
}

const LONG_LABEL: Record<RangePreset, string> = {
  "1": "Today",
  "7": "Last 7 days",
  "30": "Last 30 days",
  mtd: "This month",
  lm: "Last month",
  "90": "Last 90 days",
  "366": "Last 12 months",
};

/** The page's time range as one compact control. */
export function TimePresets({
  value,
  onChange,
}: {
  value: RangePreset | null;
  onChange: (v: RangePreset) => void;
}) {
  return (
    <Tooltip text="Days run midnight to midnight UTC.">
      <Select
        icon="calendar"
        ariaLabel="Time range"
        value={(value ?? "") as RangePreset}
        placeholder="Custom range"
        options={RANGE_PRESETS.map((p) => ({
          value: p.value,
          label: LONG_LABEL[p.value],
        }))}
        onChange={onChange}
      />
    </Tooltip>
  );
}
